'use client';

import { useEffect, useState } from 'react';

interface ShopProps {
  onClose: () => void;
}

interface ShopItem {
  id: string;
  name: string;
  description: string;
  price: number;
  type: 'blaster' | 'ship';
  stats: string;
  color: string;
  glowColor: string;
}

const BLASTERS: ShopItem[] = [
  {
    id: 'blaster_basic',
    name: 'STANDARD LASER',
    description: 'Standard blaster — hurtig og pålidelig',
    price: 0,
    type: 'blaster',
    stats: '',
    color: '#00ffe0',
    glowColor: 'rgba(0,255,224,0.4)',
  },
  {
    id: 'blaster_plasma',
    name: 'PLASMA KANON',
    description: 'Kraftfuld plasma-skud med større skade',
    price: 18000,
    type: 'blaster',
    stats: '',
    color: '#ff6600',
    glowColor: 'rgba(255,102,0,0.4)',
  },
  {
    id: 'blaster_ion',
    name: 'ION STRÅLE',
    description: 'Dobbelt skud — rammer to steder på én gang',
    price: 45000,
    type: 'blaster',
    stats: '',
    color: '#ff00ff',
    glowColor: 'rgba(255,0,255,0.4)',
  },
  {
    id: 'blaster_quantum',
    name: 'QUANTUM DESTROYER',
    description: 'Ultrakraftig kvante-stråle — ødelægger alt',
    price: 120000,
    type: 'blaster',
    stats: '',
    color: '#ffff00',
    glowColor: 'rgba(255,255,0,0.4)',
  },
];

const SHIPS: ShopItem[] = [
  {
    id: 'ship_viper',
    name: 'VIPER MK-I',
    description: 'Standard kampskib — dit første rumskib',
    price: 0,
    type: 'ship',
    stats: '',
    color: '#cc2222',
    glowColor: 'rgba(204,34,34,0.4)',
  },
  {
    id: 'ship_falcon',
    name: 'FALCON X',
    description: 'Hurtigere og mere manøvredygtig end Viper',
    price: 30000,
    type: 'ship',
    stats: '',
    color: '#00aaff',
    glowColor: 'rgba(0,170,255,0.4)',
  },
  {
    id: 'ship_titan',
    name: 'TITAN CRUISER',
    description: 'Tungt pansret krydser med kraftigt skjold',
    price: 80000,
    type: 'ship',
    stats: '',
    color: '#00ff88',
    glowColor: 'rgba(0,255,136,0.4)',
  },
  {
    id: 'ship_phantom',
    name: 'PHANTOM ELITE',
    description: 'Topklasse stealth-skib — det ultimative valg',
    price: 200000,
    type: 'ship',
    stats: '',
    color: '#cc00ff',
    glowColor: 'rgba(204,0,255,0.4)',
  },
];

function BlasterIcon({ color }: { color: string }) {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <rect x="8" y="20" width="28" height="8" rx="2" fill={color} opacity="0.9" />
      <rect x="36" y="22" width="6" height="4" rx="1" fill={color} />
      <rect x="14" y="16" width="10" height="4" rx="1" fill={color} opacity="0.7" />
      <circle cx="12" cy="24" r="4" fill={color} opacity="0.5" />
      <rect x="2" y="23" width="6" height="2" rx="1" fill={color} opacity="0.8" />
      <circle cx="3" cy="24" r="1.5" fill="white" opacity="0.9" />
    </svg>
  );
}

function ShipIcon({ color }: { color: string }) {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <path d="M24 4 L29 22 L38 22 L31 30 L34 42 L24 35 L14 42 L17 30 L10 22 L19 22 Z"
        fill={color} opacity="0.85" stroke={color} strokeWidth="1" />
      <ellipse cx="24" cy="18" rx="4" ry="6" fill="rgba(255,255,255,0.3)" />
      <circle cx="24" cy="18" r="2" fill="rgba(255,255,255,0.6)" />
    </svg>
  );
}

export default function Shop({ onClose }: ShopProps) {
  const [coins, setCoins] = useState(0);
  const [ownedItems, setOwnedItems] = useState<string[]>(['blaster_basic', 'ship_viper']);
  const [selectedBlaster, setSelectedBlaster] = useState('blaster_basic');
  const [selectedShip, setSelectedShip] = useState('ship_viper');
  const [activeTab, setActiveTab] = useState<'blaster' | 'ship'>('ship');
  const [notification, setNotification] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
    const storedCoins = parseInt(localStorage.getItem('spaceracer-coins') || '0', 10);
    const storedOwned = JSON.parse(localStorage.getItem('spaceracer-owned') || '["blaster_basic","ship_viper"]');
    const storedBlaster = localStorage.getItem('spaceracer-blaster') || 'blaster_basic';
    const storedShip = localStorage.getItem('spaceracer-ship') || 'ship_viper';
    setCoins(storedCoins);
    setOwnedItems(storedOwned);
    setSelectedBlaster(storedBlaster);
    setSelectedShip(storedShip);
  }, []);

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 2500);
  };

  const handleBuy = (item: ShopItem) => {
    if (ownedItems.includes(item.id)) {
      // Equip it
      if (item.type === 'blaster') {
        setSelectedBlaster(item.id);
        localStorage.setItem('spaceracer-blaster', item.id);
        showNotification(`✓ ${item.name} udstyret!`);
      } else {
        setSelectedShip(item.id);
        localStorage.setItem('spaceracer-ship', item.id);
        showNotification(`✓ ${item.name} udstyret!`);
      }
      return;
    }
    if (coins < item.price) {
      showNotification('✗ Ikke nok coins!');
      return;
    }
    const newCoins = coins - item.price;
    const newOwned = [...ownedItems, item.id];
    setCoins(newCoins);
    setOwnedItems(newOwned);
    localStorage.setItem('spaceracer-coins', String(newCoins));
    localStorage.setItem('spaceracer-owned', JSON.stringify(newOwned));
    // Auto-equip on purchase
    if (item.type === 'blaster') {
      setSelectedBlaster(item.id);
      localStorage.setItem('spaceracer-blaster', item.id);
    } else {
      setSelectedShip(item.id);
      localStorage.setItem('spaceracer-ship', item.id);
    }
    showNotification(`✓ ${item.name} købt og udstyret!`);
  };

  const items = activeTab === 'blaster' ? BLASTERS : SHIPS;

  return (
    <div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(0,0,0,0.92)' }}
    >
      <div
        className="relative flex flex-col w-full max-w-2xl mx-4 rounded-xl overflow-hidden"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1)' : 'scale(0.92)',
          transition: 'all 0.35s cubic-bezier(0.16,1,0.3,1)',
          background: 'linear-gradient(160deg, #02040a 0%, #060c18 100%)',
          border: '1px solid rgba(0,229,255,0.25)',
          boxShadow: '0 0 60px rgba(0,100,200,0.15), 0 0 120px rgba(0,50,100,0.08)',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid rgba(0,229,255,0.12)' }}
        >
          <div>
            <h2
              className="font-mono text-xl font-bold tracking-[0.2em] uppercase"
              style={{ color: '#00e5ff', textShadow: '0 0 12px #00e5ff' }}
            >
              🛒 BUTIK
            </h2>
            <p className="font-mono text-xs tracking-widest mt-0.5" style={{ color: 'rgba(0,229,255,0.4)' }}>
              OPGRADÉR DIT UDSTYR
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{
                background: 'rgba(255,200,0,0.08)',
                border: '1px solid rgba(255,200,0,0.3)',
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>🪙</span>
              <span
                className="font-mono font-bold text-base tracking-widest"
                style={{ color: '#ffd700', textShadow: '0 0 8px rgba(255,215,0,0.6)' }}
              >
                {coins.toLocaleString('da-DK')}
              </span>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-lg flex items-center justify-center font-mono text-lg transition-all duration-150 hover:scale-110"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex" style={{ borderBottom: '1px solid rgba(0,229,255,0.1)' }}>
          {(['ship', 'blaster'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="flex-1 py-3 font-mono text-xs tracking-[0.2em] uppercase transition-all duration-200"
              style={{
                background: activeTab === tab ? 'rgba(0,229,255,0.08)' : 'transparent',
                borderBottom: activeTab === tab ? '2px solid #00e5ff' : '2px solid transparent',
                color: activeTab === tab ? '#00e5ff' : 'rgba(255,255,255,0.35)',
              }}
            >
              {tab === 'ship' ? '🚀 RUMSKIBE' : '⚡ BLASTERS'}
            </button>
          ))}
        </div>

        {/* Items grid */}
        <div className="overflow-y-auto p-4 flex flex-col gap-3" style={{ maxHeight: '55vh' }}>
          {items.map((item) => {
            const owned = ownedItems.includes(item.id);
            const equipped = item.type === 'blaster' ? selectedBlaster === item.id : selectedShip === item.id;
            const canAfford = coins >= item.price;
            const isFree = item.price === 0;

            return (
              <div
                key={item.id}
                className="flex items-center gap-4 p-4 rounded-xl transition-all duration-200"
                style={{
                  background: equipped
                    ? `rgba(${item.color === '#cc2222' ? '204,34,34' : item.color === '#00aaff' ? '0,170,255' : item.color === '#00ff88' ? '0,255,136' : item.color === '#cc00ff' ? '204,0,255' : item.color === '#00ffe0' ? '0,255,224' : item.color === '#ff6600' ? '255,102,0' : item.color === '#ff00ff' ? '255,0,255' : '255,255,0'},0.12)`
                    : 'rgba(255,255,255,0.03)',
                  border: equipped
                    ? `1px solid ${item.color}55`
                    : owned
                    ? '1px solid rgba(255,255,255,0.1)'
                    : '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {/* Icon */}
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{
                    background: `rgba(${item.color === '#cc2222' ? '204,34,34' : item.color === '#00aaff' ? '0,170,255' : item.color === '#00ff88' ? '0,255,136' : item.color === '#cc00ff' ? '204,0,255' : item.color === '#00ffe0' ? '0,255,224' : item.color === '#ff6600' ? '255,102,0' : item.color === '#ff00ff' ? '255,0,255' : '255,255,0'},0.1)`,
                    border: `1px solid ${item.color}44`,
                    boxShadow: equipped ? `0 0 16px ${item.glowColor}` : 'none',
                  }}
                >
                  {item.type === 'blaster' ? (
                    <BlasterIcon color={item.color} />
                  ) : (
                    <ShipIcon color={item.color} />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-mono text-sm font-bold tracking-wider"
                      style={{ color: item.color, textShadow: equipped ? `0 0 8px ${item.color}` : 'none' }}
                    >
                      {item.name}
                    </span>
                    {equipped && (
                      <span
                        className="font-mono text-[10px] tracking-widest px-2 py-0.5 rounded"
                        style={{ background: `${item.color}22`, color: item.color, border: `1px solid ${item.color}44` }}
                      >
                        UDSTYRET
                      </span>
                    )}
                    {owned && !equipped && (
                      <span
                        className="font-mono text-[10px] tracking-widest px-2 py-0.5 rounded"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                      >
                        EJET
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {item.description}
                  </p>
                </div>

                {/* Buy/Equip button */}
                <div className="flex-shrink-0">
                  {equipped ? (
                    <div
                      className="px-4 py-2 rounded-lg font-mono text-xs tracking-widest"
                      style={{ background: `${item.color}18`, color: item.color, border: `1px solid ${item.color}44` }}
                    >
                      ✓ AKTIV
                    </div>
                  ) : owned ? (
                    <button
                      onClick={() => handleBuy(item)}
                      className="px-4 py-2 rounded-lg font-mono text-xs tracking-widest transition-all duration-150 hover:scale-105"
                      style={{
                        background: 'rgba(0,229,255,0.08)',
                        color: '#00e5ff',
                        border: '1px solid rgba(0,229,255,0.3)',
                      }}
                    >
                      UDSTYR
                    </button>
                  ) : (
                    <button
                      onClick={() => handleBuy(item)}
                      disabled={!canAfford}
                      className="flex flex-col items-center px-4 py-2 rounded-lg font-mono text-xs tracking-widest transition-all duration-150"
                      style={{
                        background: canAfford ? 'rgba(255,200,0,0.1)' : 'rgba(255,255,255,0.03)',
                        color: canAfford ? '#ffd700' : 'rgba(255,255,255,0.2)',
                        border: canAfford ? '1px solid rgba(255,200,0,0.35)' : '1px solid rgba(255,255,255,0.08)',
                        cursor: canAfford ? 'pointer' : 'not-allowed',
                        transform: canAfford ? undefined : 'none',
                      }}
                    >
                      <span style={{ fontSize: '0.9rem' }}>🪙</span>
                      <span>{isFree ? 'GRATIS' : item.price.toLocaleString('da-DK')}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Notification */}
        {notification && (
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 rounded-xl font-mono text-sm tracking-widest"
            style={{
              background: notification.startsWith('✓') ? 'rgba(0,200,100,0.15)' : 'rgba(255,50,50,0.15)',
              border: notification.startsWith('✓') ? '1px solid rgba(0,200,100,0.4)' : '1px solid rgba(255,50,50,0.4)',
              color: notification.startsWith('✓') ? '#00c864' : '#ff4444',
              animation: 'fadeInOut 2.5s ease-in-out forwards',
              whiteSpace: 'nowrap',
            }}
          >
            {notification}
          </div>
        )}

        {/* Hint */}
        <div
          className="px-6 py-3 font-mono text-[10px] tracking-widest text-center"
          style={{ color: 'rgba(255,255,255,0.2)', borderTop: '1px solid rgba(255,255,255,0.05)' }}
        >
          💡 TIP: Tjen coins ved at spille — point omdannes til coins efter hvert spil
        </div>
      </div>

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateX(-50%) translateY(8px); }
          15% { opacity: 1; transform: translateX(-50%) translateY(0); }
          75% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-4px); }
        }
      `}</style>
    </div>
  );
}
